import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import AdForgeLogo from '../../../components/feature/AdForgeLogo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBg = scrolled || !isHome
    ? 'bg-white/95 backdrop-blur-sm border-b border-stone-200'
    : 'bg-transparent';

  const textColor = scrolled || !isHome ? 'text-stone-700' : 'text-white';
  const hoverColor = scrolled || !isHome ? 'hover:text-orange-500' : 'hover:text-orange-300';

  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'About', href: '/about' },
    { label: 'Dashboard', href: '/dashboard' },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <AdForgeLogo size="lg" light={!scrolled && isHome} />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 ${textColor} ${hoverColor}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button
                onClick={handleLogout}
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled || !isHome
                    ? 'text-stone-700 hover:text-orange-500'
                    : 'text-white hover:text-orange-300'
                }`}
              >
                Log Out
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200 whitespace-nowrap ${
                    scrolled || !isHome
                      ? 'text-stone-700 hover:text-orange-500'
                      : 'text-white hover:text-orange-300'
                  }`}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center ${textColor}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-sm font-medium text-stone-700 hover:text-orange-500"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <hr className="border-stone-100" />
          {user ? (
            <button
              onClick={() => { handleLogout(); setMenuOpen(false); }}
              className="text-sm font-medium text-stone-700 hover:text-orange-500 text-left cursor-pointer whitespace-nowrap"
            >
              Log Out
            </button>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-stone-700 hover:text-orange-500" onClick={() => setMenuOpen(false)}>Log In</Link>
              <Link to="/signup" className="text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-white text-center hover:bg-orange-600" onClick={() => setMenuOpen(false)}>Get Started Free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
